import { Controller } from "@hotwired/stimulus"

// One live form, still in its Stimulus scope. Lift it out of the scroller's clipping edge
// as it reaches the header, then clamp it there. Scroll position drives both directions.
export default class extends Controller {
  static targets = ["anchor", "form"]

  connect() {
    this.header = document.querySelector(".search-v2-header")
    this.nav = this.header?.querySelector(".search-v2-header__nav--search")
    this.slot = this.nav?.querySelector(".search-v2-header__search-slot")
    if (!this.slot) return

    this.brand = this.nav.querySelector(".search-v2-header__brand")
    this.actions = this.nav.querySelector(".search-v2-header__actions")
    this.observer = new ResizeObserver(() => this.resize())
    for (const element of [this.header, this.brand, this.actions, this.anchorTarget, this.formTarget]) this.observer.observe(element)
    this.resize()
  }

  resize() {
    if (!this.slot || !this.element.getClientRects().length) return
    const height = this.formTarget.offsetHeight
    this.anchorTarget.style.minHeight = `${height}px`
    this.nav.style.setProperty("--search-form-height", `${height}px`)

    // Equal grid wings keep the center on the page, not halfway between unequal logo/actions.
    const width = this.nav.clientWidth
    const actionWidth = this.actions.offsetWidth
    const sideWidth = Math.max(this.brand.offsetWidth, actionWidth)
    this.compact = width - 2 * (sideWidth + 24) < 320
    const gap = this.compact ? 12 : 24
    const wing = this.compact ? actionWidth : sideWidth
    this.nav.style.setProperty("--search-dock-width", `${Math.max(0, Math.min(640, width - 2 * (wing + gap)))}px`)
    this.nav.style.setProperty("--search-dock-gap", `${gap}px`)
    this.update()
  }

  update() {
    if (!this.slot) return
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => this.measure())
  }

  measure() {
    if (!this.element.getClientRects().length) return
    const anchor = this.anchorTarget.getBoundingClientRect()
    const slot = this.slot.getBoundingClientRect()
    const edge = this.element.getBoundingClientRect().top
    const handoffStart = edge + slot.height
    const floating = anchor.top < handoffStart
    this.header.classList.toggle("search-v2-header--search-compact", anchor.top < slot.bottom && this.compact)
    if (!floating) {
      this.clearPosition()
      return
    }

    // Enter fixed positioning at the exact same coordinates as the form in the page.
    // Finish narrowing before entering the nav row, so History never overlaps the input.
    // The same scroll-linked path in reverse leaves the form in its original page position.
    const progress = Math.min(1, (handoffStart - anchor.top) / Math.max(1, handoffStart - slot.bottom))
    const width = anchor.width + (slot.width - anchor.width) * progress
    const center = anchor.left + anchor.width / 2
    this.formTarget.classList.add("provider-landing__search-docked")
    Object.assign(this.formTarget.style, {
      top: `${Math.max(anchor.top, slot.top)}px`,
      left: `${center - width / 2}px`,
      width: `${width}px`
    })
  }

  clearPosition() {
    if (!this.hasFormTarget) return
    this.formTarget.classList.remove("provider-landing__search-docked")
    for (const property of ["left", "top", "width"]) this.formTarget.style.removeProperty(property)
  }

  reset() {
    cancelAnimationFrame(this.frame)
    this.header?.classList.remove("search-v2-header--search-compact")
    this.clearPosition()
  }

  disconnect() {
    this.observer?.disconnect()
    this.reset()
  }
}
